/**
 * Agent-file cards — pure extraction logic (no React, no fetch), shared by
 * every Agent SDK door (admin console, Quality Co-Pilot, Tech Support,
 * Analyst portal).
 *
 * `attach_file` is the agent→user file channel: the agent generates a file
 * (a matplotlib chart, a CSV/XLSX extract, a write-up) in its scratch dir and
 * attaches it; the backend stores it durably and acks with a machine-liftable
 * signature line (services/agent_console/tools/attach.py::format_attach_ack):
 *
 *     attached_file id=<uuid> name='<filename>' type=<mime> size=<bytes>
 *
 * This module lifts SUCCESSFUL attach frames out of a turn's block trace into
 * file-card models (the card replaces the chip). A pending call (no result
 * yet, mid-stream) or a failed one keeps its chip, so the trace still shows
 * what happened. A turn can attach several files — the lift ACCUMULATES.
 */

import type { AssistantBlock } from '../../services/agentConsole';

export interface AgentFileData {
  attachmentId: string;
  filename: string;
  /** Display name — the tool input's `title` when given, else the filename. */
  title: string;
  mimeType: string;
  sizeBytes: number;
}

export function isAttachFileTool(name: string): boolean {
  return name === 'attach_file' || name.endsWith('__attach_file');
}

// Mirrors format_attach_ack — the backend test test_attach_tool.py pins the
// same signature, so drift breaks a test on whichever side moved.
const ATTACH_ACK_RE =
  /attached_file id=([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}) name=(['"])(.+?)\2 type=(\S+) size=(\d+)/;

/** Parse a SUCCESS ack into a card model; null when it doesn't match. */
export function agentFileFromResult(
  summary: string | null | undefined,
  input?: Record<string, unknown>,
): AgentFileData | null {
  if (!summary) return null;
  const match = ATTACH_ACK_RE.exec(summary);
  if (!match) return null;
  const filename = match[3];
  const rawTitle = input?.title;
  const title =
    typeof rawTitle === 'string' && rawTitle.trim() ? rawTitle.trim() : filename;
  return {
    attachmentId: match[1],
    filename,
    title,
    mimeType: match[4],
    sizeBytes: Number(match[5]),
  };
}

export interface LiftedAgentFiles {
  blocks: AssistantBlock[];
  files: AgentFileData[];
}

/**
 * Pull every SUCCESSFUL attach_file frame out of the blocks (live after each
 * tool_result, and on resume over the reconstructed trace). Pending/errored
 * frames keep their chips.
 */
export function liftAgentFileCards(blocks: AssistantBlock[]): LiftedAgentFiles {
  const files: AgentFileData[] = [];
  const kept: AssistantBlock[] = [];
  for (const block of blocks) {
    if (block.kind === 'tool' && isAttachFileTool(block.tool.rawName)) {
      const file =
        block.tool.status === 'error'
          ? null
          : agentFileFromResult(block.tool.resultSummary, block.tool.input);
      if (file) {
        files.push(file);
        continue; // card replaces the chip
      }
    }
    kept.push(block);
  }
  return { blocks: kept, files };
}

/** Append newly lifted files, deduped by attachment id (order preserved). */
export function mergeAgentFiles(
  existing: AgentFileData[] | undefined,
  lifted: AgentFileData[],
): AgentFileData[] {
  const seen = new Set((existing ?? []).map((f) => f.attachmentId));
  const merged = [...(existing ?? [])];
  for (const file of lifted) {
    if (!seen.has(file.attachmentId)) {
      seen.add(file.attachmentId);
      merged.push(file);
    }
  }
  return merged;
}

export function isPreviewableImage(mimeType: string): boolean {
  // SVG previews via <img> too — scripts never execute in an image context.
  return mimeType.startsWith('image/');
}

export function formatFileSize(sizeBytes: number): string {
  if (sizeBytes >= 1024 * 1024) return `${(sizeBytes / (1024 * 1024)).toFixed(1)} MB`;
  if (sizeBytes >= 1024) return `${(sizeBytes / 1024).toFixed(1)} KB`;
  return `${sizeBytes} B`;
}

/**
 * Plain-language file kind for the card's sub line, so the reader knows what
 * they are downloading before they open it (a Windows user handed a
 * `text/markdown` file has no app for it — see kanban 9e11b8c8).
 */
export function fileKindLabel(mimeType: string): string {
  const mime = mimeType.split(';')[0].trim().toLowerCase();
  if (mime === 'application/pdf') return 'PDF';
  if (mime === 'text/csv') return 'CSV';
  if (mime.includes('spreadsheetml') || mime === 'application/vnd.ms-excel') return 'Excel';
  if (mime.includes('wordprocessingml') || mime === 'application/msword') return 'Word';
  if (mime === 'text/markdown') return 'Markdown text';
  if (mime === 'text/plain') return 'Text';
  if (mime === 'application/json') return 'JSON';
  if (mime === 'image/svg+xml') return 'SVG image';
  if (mime.startsWith('image/')) return 'Image';
  return 'File';
}

/** Emoji for the card's file-type badge. */
export function fileKindIcon(mimeType: string): string {
  if (mimeType.startsWith('image/')) return '🖼️';
  if (mimeType === 'text/csv' || mimeType.includes('spreadsheetml')) return '📊';
  if (mimeType === 'application/pdf') return '📕';
  if (mimeType.includes('wordprocessingml')) return '📝';
  return '📄';
}
